import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhaserGame } from '../phaser';
import { GameConfigService, GameConfig } from '../services/gameConfigService';
import { useAuth } from '../contexts/AuthContext';
import './CloudTowerGamePage.css';
import { BaseLayout, GamePopup } from '@/components';
import LeaderboardModal from '../components/LeaderboardModal';

const CloudTowerGamePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showGame, setShowGame] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const phaserGameRef = useRef<PhaserGame | null>(null);
  const [isGameLoaded, setIsGameLoaded] = useState(false);

  // Fetch game config when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchGameConfig();
    }
  }, [isAuthenticated]);

  const fetchGameConfig = async () => {
    setIsLoading(true);
    try {
      const config = await GameConfigService.getGameConfig();
      setGameConfig(config);
    } catch (error) {
      console.error('Error fetching game config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartGame = () => {
    setShowGame(true);
  };

  const handleLeaderboardClick = () => {
    setShowLeaderboard(true);
  };

  const handleCloseLeaderboard = () => {
    setShowLeaderboard(false);
  };

  useEffect(() => {
    if (gameContainerRef.current && !phaserGameRef.current && showGame) {
      try {
        // Khởi tạo Phaser game
        phaserGameRef.current = new PhaserGame(gameContainerRef.current);
        
        // Bắt đầu game cloud-tower
        phaserGameRef.current.startGame('cloud-tower');
        
        setIsGameLoaded(true);
      } catch (error) {
        console.error('Error initializing game:', error);
      }
    }

    // Cleanup khi component unmount
    return () => {
      if (phaserGameRef.current) {
        phaserGameRef.current.destroy();
        phaserGameRef.current = null;
      }
    };
  }, [showGame]);

  const handleBackClick = () => {
    // Destroy game trước khi navigate
    if (phaserGameRef.current) {
      phaserGameRef.current.destroy();
      phaserGameRef.current = null;
    }
    navigate(-1);
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="cloud-tower-game-page">
        <div className="game-header">
          <button className="game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="game-title">KILOBA 9 Tầng Mây</h1>
          <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
            <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
          </button>
        </div>
        
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Đang tải...</p>
        </div>
        
        <LeaderboardModal 
          isOpen={showLeaderboard} 
          onClose={handleCloseLeaderboard} 
          gameType="kiloba_9_clouds" 
        />
      </div>
    );
  }

  // Welcome screen with game instructions
  if (!showGame && gameConfig) {
    const remainingAttempts = GameConfigService.getRemainingAttempts(gameConfig, 'clouds');
    const canPlay = GameConfigService.canPlayGame(gameConfig, 'clouds');
    const energyReward = GameConfigService.getEnergyReward(gameConfig, 'clouds');
    const requiredScore = gameConfig.clouds.requiredScore;

    return (
      <div className="cloud-tower-game-page">
        <div className="game-header">
          <button className="game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="game-title">KILOBA 9 Tầng Mây</h1>
          <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
            <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
          </button>
        </div>
        
        <div className="welcome-container">
          <div className="welcome-content">
            <div className="quiz-info">
              <h2>Chào mừng đến với Kiloba 9 Tầng Mây!</h2>
              <div className="quiz-rules">
                <p>• Nhấn vào màn hình để điều khiển Kiloba nhảy</p>
                <p>• Leo lên các tầng mây cao nhất có thể</p>
                <p>• Tránh rơi xuống đất để tiếp tục trò chơi</p>
                <p>• Cần đạt {requiredScore} điểm để nhận {energyReward} năng lượng</p>
                <p>• Giới hạn: {gameConfig.clouds.dailyPlayLimit} lượt nhận thưởng/ngày</p>
              </div>
              <div className="attempts-info">
                <p>Lượt chơi còn lại: <strong>{remainingAttempts}/{gameConfig.clouds.dailyPlayLimit}</strong></p>
              </div>
            </div>
            
            <button 
              className="start-quiz-button"
              onClick={handleStartGame}
              disabled={!canPlay || remainingAttempts <= 0}
            >
              {!canPlay || remainingAttempts <= 0 ? 'Hết lượt chơi' : 'Bắt đầu chơi'}
            </button>
          </div>
        </div>
        
        <LeaderboardModal 
          isOpen={showLeaderboard} 
          onClose={handleCloseLeaderboard} 
          gameType="kiloba_9_clouds" 
        />
      </div>
    );
  }

  // Game screen
  if (showGame) {
    return (
      <div className="cloud-tower-game-page">
        <GamePopup />
        <div className="game-header">
          <button className="game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="game-title">KILOBA 9 Tầng Mây</h1>
          <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
            <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
          </button>
        </div>
        
        <div className="game-container-wrapper">
          {!isGameLoaded && (
            <div className="game-loading">
              <div className="loading-spinner"></div>
              <p>Đang tải game...</p>
            </div>
          )}
          <div 
            ref={gameContainerRef} 
            className="game-container"
            style={{ opacity: isGameLoaded ? 1 : 0 }}
          />
        </div>
        
        <LeaderboardModal 
          isOpen={showLeaderboard} 
          onClose={handleCloseLeaderboard} 
          gameType="kiloba_9_clouds" 
        />
      </div>
    );
  }

  // Default fallback
  return (
    <div className="cloud-tower-game-page">
      <div className="game-header">
        <button className="game-back-btn" onClick={handleBackClick}>
          <img src="/images/icons/back.svg" alt="Back" />
        </button>
        <h1 className="game-title">KILOBA 9 Tầng Mây</h1>
        <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
          <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
        </button>
      </div>
      
      <div className="loading-container">
        <p>Đang tải...</p>
      </div>
      
      <LeaderboardModal 
        isOpen={showLeaderboard} 
        onClose={handleCloseLeaderboard} 
        gameType="kiloba_9_clouds" 
      />
    </div>
  );
};

const CloudTowerGamePageContainer: React.FC = () => {
  return (
    <div className="viewport-wrapper">
      <BaseLayout className="common-container" backgroundColor="transparent">
        <CloudTowerGamePage />
      </BaseLayout>
    </div>
  );
}

export default CloudTowerGamePageContainer;
